import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import { DateUtil } from '@src/packages/utils/dates';

/** Edit unlocks after 24 hours from the latest attendance mark. */
export const ATTENDANCE_EDIT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type AttendanceEditBlockReason = 'journey_not_finished' | 'cooldown';

/**
 * Reference instant for the edit cooldown: the most recent actual mark,
 * falling back to `scheduledStartAt` when no marks exist yet (e.g. blank
 * matrix-created absences). Never use `createdAt` — creating a past-day
 * record today would incorrectly lock edit for 24h.
 */
export const getAttendanceMarkingReferenceMs = (
  record: IAttendanceRecord | null | undefined,
): number | null => {
  if (!record) return null;

  const actualMarkMs = [
    DateUtil.getTimestampMs(record.actualStartAt),
    DateUtil.getTimestampMs(record.actualBreakStartAt),
    DateUtil.getTimestampMs(record.actualBreakEndAt),
    DateUtil.getTimestampMs(record.actualEndAt),
  ].filter((ms): ms is number => ms != null);

  if (actualMarkMs.length > 0) {
    return Math.max(...actualMarkMs);
  }

  return DateUtil.getTimestampMs(record.scheduledStartAt);
};

export const isAttendanceEditAvailable = (
  markingMs: number | null,
  nowMs: number = Date.now(),
): boolean => {
  if (markingMs == null) return false;
  return nowMs - markingMs >= ATTENDANCE_EDIT_COOLDOWN_MS;
};

/** End-of-day for an ISO `YYYY-MM-DD` in the app default timezone (org / Chile). */
export const getScheduledDayEndMs = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;
  return DateUtil.getCalendarDateBoundsMillis(isoDate)?.endMillis ?? null;
};

/**
 * When the scheduled work journey ends for edit gating.
 * Prefer AR `scheduledEndAt`; fall back to calendar day end for `scheduledDate`.
 */
export const getJourneyEndMs = (
  record: IAttendanceRecord | null | undefined,
  scheduledDate?: string | null,
): number | null => {
  const fromRecord = DateUtil.getTimestampMs(record?.scheduledEndAt);
  if (fromRecord != null) return fromRecord;
  return getScheduledDayEndMs(scheduledDate);
};

/**
 * True while the work journey for this cell has not finished yet (today during
 * the shift, or a future scheduled day). Matrix edit stays locked; use Real-Time
 * Attendance for the live shift.
 */
export const isJourneyNotYetFinished = (
  record: IAttendanceRecord | null | undefined,
  scheduledDate: string | null | undefined,
  nowMs: number = Date.now(),
): boolean => {
  const endMs = getJourneyEndMs(record, scheduledDate);
  if (endMs == null) return false;
  return nowMs <= endMs;
};

export type CanEditAttendanceMarkingOptions = {
  /** Matrix cell ISO date (`YYYY-MM-DD`). Used when AR has no `scheduledEndAt`. */
  scheduledDate?: string | null;
};

/**
 * Why matrix "Edit" is disabled, or `null` when edit is allowed.
 * Order: journey must end first, then the 24h mark cooldown.
 */
export const getAttendanceEditBlockReason = (
  record: IAttendanceRecord | null | undefined,
  nowMs: number = Date.now(),
  options: CanEditAttendanceMarkingOptions = {},
): AttendanceEditBlockReason | null => {
  if (isJourneyNotYetFinished(record, options.scheduledDate, nowMs)) {
    return 'journey_not_finished';
  }
  if (!isAttendanceEditAvailable(getAttendanceMarkingReferenceMs(record), nowMs)) {
    return 'cooldown';
  }
  return null;
};

export const canEditAttendanceMarking = (
  record: IAttendanceRecord | null | undefined,
  nowMs: number = Date.now(),
  options: CanEditAttendanceMarkingOptions = {},
): boolean => getAttendanceEditBlockReason(record, nowMs, options) == null;

/**
 * Earliest instant when matrix edit becomes available, or `null` when already editable
 * / unlock cannot be determined. Combines journey end + 24h mark cooldown.
 */
export const getAttendanceEditUnlockMs = (
  record: IAttendanceRecord | null | undefined,
  nowMs: number = Date.now(),
  options: CanEditAttendanceMarkingOptions = {},
): number | null => {
  if (canEditAttendanceMarking(record, nowMs, options)) {
    return null;
  }

  const journeyEndMs = getJourneyEndMs(record, options.scheduledDate);
  const markingMs = getAttendanceMarkingReferenceMs(record);
  const cooldownUnlockMs =
    markingMs != null ? markingMs + ATTENDANCE_EDIT_COOLDOWN_MS : null;

  const candidates = [journeyEndMs != null ? journeyEndMs + 1 : null, cooldownUnlockMs].filter(
    (ms): ms is number => ms != null,
  );

  if (candidates.length === 0) return null;
  return Math.max(...candidates);
};
